import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import type { PageSuggestion, PersonalPage } from "@ncp/page-host";

/** One version of a page as the authoring API returns it (the owner of a personal page is its author). */
export interface PageVersion {
  id: string;
  key: string;
  version: number;
  status: "Draft" | "Published" | "Retired" | string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  spaces: string[];
  slots: string[];
  visibility: "Space" | "Personal" | string;
  origin: string;
  updatedAt: string;
  publishedAt?: string | null;
}

export interface MyPageDetail {
  page: PageVersion;
  versions: PageVersion[];
}

export interface PageShare {
  userId: string;
  name?: string | null;
  nameAr?: string | null;
  sharedAt: string;
}

export interface NewPersonalPageInput {
  title: string;
  titleAr?: string | null;
  purpose?: string | null;
  template?: string | null;
  space?: string | null;
}

export interface ImproveResult {
  suggestion: PageSuggestion;
  definitionId: string;
  previewKey: string;
}

const enc = encodeURIComponent;

export const myPageKeys = {
  all: ["my-pages"] as const,
  list: ["my-pages", "list"] as const,
  detail: (key: string) => ["my-pages", "detail", key] as const,
  shares: (key: string) => ["my-pages", "shares", key] as const,
  suggestions: ["my-pages", "suggestions"] as const,
};

/** The person's own pages, then the ones colleagues shared with them. */
export function usePersonalPages(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: myPageKeys.list, queryFn: () => api.get<PersonalPage[]>("/api/pages/personal"), enabled, staleTime: 30_000 });
}

/** The newest version of one of the person's own pages (draft or released); colleagues it is shared with cannot read it. */
export function useMyPage(key: string | null, enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: myPageKeys.detail(key ?? ""), queryFn: () => api.get<MyPageDetail>(`/api/platform/pages/${enc(key!)}`), enabled: !!key && enabled, staleTime: 5_000 });
}

export function useMySuggestions(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: myPageKeys.suggestions, queryFn: () => api.get<PageSuggestion[]>("/api/pages/suggestions/mine"), enabled, staleTime: 10_000 });
}

export function usePageShares(key: string | null, enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: myPageKeys.shares(key ?? ""), queryFn: () => api.get<PageShare[]>(`/api/pages/personal/${enc(key!)}/shares`), enabled: !!key && enabled });
}

/** Everything the owner does to a personal page, and the suggestion flow; each refreshes the lists it changes. */
export function useMyPageActions() {
  const api = useApi();
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: myPageKeys.all });
  const opts = { onSuccess: refresh };
  return {
    create: useMutation({ mutationFn: (input: NewPersonalPageInput) => api.post<PageVersion>("/api/pages/personal", input), ...opts }),
    edit: useMutation({ mutationFn: (key: string) => api.post<PageVersion>(`/api/platform/pages/${enc(key)}/draft`), ...opts }),
    /** The titles live in the draft's page.json; saving it renames the page. */
    rename: useMutation({
      mutationFn: async ({ definitionId, title, titleAr }: { definitionId: string; title: string; titleAr: string }) => {
        const file = await api.get<{ content: string }>(`/api/platform/pages/versions/${definitionId}/files/page.json`);
        const manifest = JSON.parse(file.content) as Record<string, unknown>;
        manifest.title = { ...(manifest.title as Record<string, unknown> | undefined), en: title, ar: titleAr };
        return api.put<MyPageDetail>(`/api/platform/pages/versions/${definitionId}/files`, {
          upserts: [{ path: "page.json", content: `${JSON.stringify(manifest, null, 2)}\n` }],
          changeNote: "Renamed",
        });
      },
      ...opts,
    }),
    release: useMutation({ mutationFn: (definitionId: string) => api.post<PageVersion>(`/api/platform/pages/versions/${definitionId}/publish-personal`), ...opts }),
    discard: useMutation({ mutationFn: (definitionId: string) => api.delete<void>(`/api/platform/pages/versions/${definitionId}`), ...opts }),
    remove: useMutation({ mutationFn: (key: string) => api.delete<void>(`/api/pages/personal/${enc(key)}`), ...opts }),
    share: useMutation({ mutationFn: ({ key, userIds }: { key: string; userIds: string[] }) => api.post<PageShare[]>(`/api/pages/personal/${enc(key)}/shares`, { userIds }), ...opts }),
    unshare: useMutation({ mutationFn: ({ key, userId }: { key: string; userId: string }) => api.delete<void>(`/api/pages/personal/${enc(key)}/shares/${userId}`), ...opts }),
    promote: useMutation({
      mutationFn: ({ key, space, targetKey, note }: { key: string; space: string; targetKey?: string | null; note?: string | null }) =>
        api.post<PageSuggestion>(`/api/pages/personal/${enc(key)}/promote`, { space, targetKey: targetKey || null, note: note || null }),
      ...opts,
    }),
    improve: useMutation({
      mutationFn: ({ key, request, space }: { key: string; request: string; space?: string | null }) => api.post<ImproveResult>(`/api/pages/${enc(key)}/suggestions`, { request, space: space ?? null }),
      ...opts,
    }),
    submit: useMutation({ mutationFn: ({ id, summary }: { id: string; summary?: string | null }) => api.post<PageSuggestion>(`/api/pages/suggestions/${id}/submit`, { summary: summary || null }), ...opts }),
    withdraw: useMutation({ mutationFn: (id: string) => api.post<PageSuggestion>(`/api/pages/suggestions/${id}/withdraw`), ...opts }),
  };
}
